import { useCallback } from 'react';
import { Step, Workflow } from './Workflow';
import useFeature from 'hooks/useFeature';
import { selectTenant, FeatureGroups } from 'state/tenant';
import { useSelector } from 'react-redux';
import useResponsiveQuery from 'hooks/useResponsiveQuery';
import { reject, not, keys } from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';

const useFmcWorkflow = () => {
  const tenant = useSelector(selectTenant);
  const { overrideConfig } = tenant;
  const { isGroupEnabled } = useFeature();
  const { isMobile } = useResponsiveQuery();
  const { selectedCardKey, magicLinkId, magicLinkOtp } = useQueryParameters();
  const { isEnabled } = useFeature();

  const getWorkflow = useCallback((): Workflow => {
    const hasCardChoice = tenant.enabledFeatures.cardChoices.length > 0;
    const hasProductChoice = tenant.enabledFeatures.productChoices.length > 0;

    const hasMagicLinkAcceptance =
      tenant.enabledFeatures.magicLinkVerification &&
      overrideConfig.phoneLookup &&
      Boolean(magicLinkId) &&
      Boolean(magicLinkOtp);

    const hasMagicLinkVerification =
      overrideConfig.phoneLookup &&
      Boolean(tenant.enabledFeatures.magicLinkVerification);

    const hasConsumerSearch =
      !hasMagicLinkVerification &&
      (overrideConfig.tokenLookup || overrideConfig.phoneLookup);

    const stepsEnabled = {
      [Step.CARD_SELECTOR]: isEnabled('cardSelector') && !selectedCardKey,

      [Step.PRODUCT_CHOICE]: hasProductChoice,

      [Step.CARD_CHOICE]: hasCardChoice,

      [Step.MAGIC_LINK_VERIFICATION]: hasMagicLinkVerification,

      [Step.CONSUMER_SEARCH]: hasConsumerSearch,

      [Step.MAGIC_LINK_ACCEPT]: hasMagicLinkAcceptance,

      [Step.APPLICATION]: true,

      [Step.APPLICATION_2]: isMobile,

      [Step.ADD_ONS]: isGroupEnabled(FeatureGroups['add-ons']),

      [Step.REVIEW_TERMS]: true,
    };

    const progressStartStep = Step.APPLICATION;

    const firstStep = ((keys(
      reject(not, stepsEnabled),
    ) as unknown) as Step[])[0];

    const paths = {
      [Step.CARD_SELECTOR]: '/card-selector',
      [Step.PRODUCT_CHOICE]: '/product-choice',
      [Step.CARD_CHOICE]: '/card-choice',
      [Step.MAGIC_LINK_VERIFICATION]: '/magic-link-verification',
      [Step.CONSUMER_SEARCH]: '/consumer-search',
      [Step.MAGIC_LINK_ACCEPT]: '/magic-link-accept',
      [Step.APPLICATION]: '/credit-application',
      [Step.APPLICATION_2]: '/credit-application/2',
      [Step.ADD_ONS]: '/add-ons',
      [Step.REVIEW_TERMS]: '/review-terms',
      ...{
        [firstStep]: '/',
      },
    };

    return {
      stepsEnabled,
      paths,
      progressStartStep,
      restartStep: firstStep,
      timeoutRestartPath: '/',
    };
  }, [
    tenant.enabledFeatures.cardChoices.length,
    tenant.enabledFeatures.productChoices.length,
    tenant.enabledFeatures.magicLinkVerification,
    overrideConfig.phoneLookup,
    overrideConfig.tokenLookup,
    magicLinkId,
    magicLinkOtp,
    isEnabled,
    selectedCardKey,
    isMobile,
    isGroupEnabled,
  ]);

  return getWorkflow;
};

export default useFmcWorkflow;
